import { useEffect } from 'react';

import { useObservability } from 'platform/observability';
import { isAvailableProduct } from 'sellDirect/declarations/products';
import type { SellerProducts } from 'sellDirect/graphql/useGetSellerProductsExperiences';
import type { SellerTransaction } from 'sellDirect/graphql/useSellerTransaction';

type TransactionWithAcquisitionProducts = Extract<
  SellerTransaction,
  { __typename: 'QualifiedTransaction' | 'InContractTransaction' | 'WithdrawnTransaction' }
>;

const isTransactionWithAcquisitionProducts = (
  sellerTransaction: SellerTransaction,
): sellerTransaction is TransactionWithAcquisitionProducts =>
  sellerTransaction.__typename === 'QualifiedTransaction' ||
  sellerTransaction.__typename === 'InContractTransaction' ||
  sellerTransaction.__typename === 'WithdrawnTransaction';

const legacyHasAnyProducts = (sellerProducts: SellerProducts): boolean =>
  !!(
    sellerProducts.cashOfferProduct ||
    sellerProducts.cashPlusProduct ||
    sellerProducts.listingProduct
  );

type Params = {
  sellerTransaction: SellerTransaction | null;
  sellerTransactionLoading: boolean;
  sellerProducts: SellerProducts;
  sellerProductsLoading: boolean;
  sellerLeadUuid: string | undefined;
};

/**
 * Shadow-compares product data from sellerTransaction.acquisitionProducts against
 * the legacy productsExperiences source. Logs discrepancies to aid migration confidence.
 * Remove once sellerTransaction is the sole source of truth.
 */
export const useShadowCompareSellerProducts = ({
  sellerTransaction,
  sellerTransactionLoading,
  sellerProducts,
  sellerProductsLoading,
  sellerLeadUuid,
}: Params): void => {
  const { trackError } = useObservability();

  useEffect(() => {
    if (sellerTransactionLoading || sellerProductsLoading) {
      return;
    }

    if (!sellerTransaction || !isTransactionWithAcquisitionProducts(sellerTransaction)) {
      return;
    }

    const { acquisitionProducts, selectedAcquisitionProduct } = sellerTransaction;
    const txHasProducts = !!(
      acquisitionProducts.sellDirect ||
      acquisitionProducts.sellWithUpside ||
      acquisitionProducts.listing
    );
    const legacyHasProducts = legacyHasAnyProducts(sellerProducts);

    // One source has products and the other doesn't
    if (legacyHasProducts !== txHasProducts) {
      trackError('seller-products-shadow-discrepancy', undefined, {
        sellerLeadUuid,
        discrepancies: {
          hasProducts: { legacy: legacyHasProducts, transaction: txHasProducts },
        },
      });
      return;
    }

    if (!txHasProducts) {
      return;
    }

    const discrepancies: Record<string, unknown> = {};

    const legacyOfferUuid = sellerProducts.cashOfferProduct?.offerUuid ?? null;
    const txOfferUuid = acquisitionProducts.sellDirect?.offerUuid ?? null;
    if (legacyOfferUuid !== txOfferUuid) {
      discrepancies.cashOfferProduct = { legacy: legacyOfferUuid, tx: txOfferUuid };
    }

    const legacyCashPlusUuid = sellerProducts.cashPlusProduct?.pricing?.uuid ?? null;
    const txCashPlusUuid = acquisitionProducts.sellWithUpside?.pricing?.uuid ?? null;
    if (legacyCashPlusUuid !== txCashPlusUuid) {
      discrepancies.cashPlusProduct = { legacy: legacyCashPlusUuid, tx: txCashPlusUuid };
    }

    const legacyListingId = sellerProducts.listingProduct?.id ?? null;
    const txListingId = acquisitionProducts.listing?.id ?? null;
    if (legacyListingId !== txListingId) {
      discrepancies.listingProduct = { legacy: legacyListingId, tx: txListingId };
    }

    // sellerProducts.selectedProduct is filtered through isAvailableProduct, so
    // LIST_WITH_CERTAINTY maps to undefined there but may appear in selectedAcquisitionProduct.
    // Apply the same filter to avoid false positives.
    const legacySelected = sellerProducts.selectedProduct ?? null;
    const txKind = selectedAcquisitionProduct?.kind;
    const txSelected = txKind && isAvailableProduct(txKind) ? txKind : null;
    if (legacySelected !== txSelected) {
      discrepancies.selectedProduct = { legacy: legacySelected, transaction: txSelected };
    }

    if (Object.keys(discrepancies).length > 0) {
      trackError('seller-products-shadow-discrepancy', undefined, {
        sellerLeadUuid,
        discrepancies,
      });
    }
  }, [
    sellerTransaction,
    sellerTransactionLoading,
    sellerProducts,
    sellerProductsLoading,
    sellerLeadUuid,
    trackError,
  ]);
};
